import * as CL from '@design-system/component-library';
import { Link, generatePath } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import {
  customerMsg,
  monthlyChargesMsg,
  oneTimePaymentMsg,
  orderDateMsg,
  orderMsg,
  recurringChargesMsg,
  statusMsg,
  t,
} from '../../common/i18n/index.js';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils.js';
import { getEmployeePeriodicPriceAsText, getPriceAsText } from '../../common/utils/priceUtils';
import {
  getDeliveryOrderStatusInfo,
  orderContainsBillingPeriod,
} from '../CustomerOrderDetails/CustomerOrderDetailsUtil.js';
import { Status } from '../Status/Status.js';
import { TrackingCodes } from '../TrackingCodes/TrackingCodes';
import type { CustomerOrder, DeliveryOrder, DeliveryOrderDetails } from '../../generated/api/models.js';

interface DeliveryOrderDetailsGeneralProps {
  customerOrder: CustomerOrder;
  deliveryOrder: DeliveryOrder;
  details: DeliveryOrderDetails;
  onClickCustomerOrder: () => void;
  isApprover: boolean;
  mdmId?: string;
}

export const DeliveryOrderDetailsGeneral = ({
  customerOrder,
  deliveryOrder,
  details,
  onClickCustomerOrder,
  isApprover,
  mdmId,
}: DeliveryOrderDetailsGeneralProps) => {
  const { customerOrderDisplayId } = customerOrder;
  const {
    deliveryOrderCreated,
    monthlyRecurringCharge,
    oneTimeCharge,
    status,
    eppOrder,
    employeeMonthlyRecurringCharge,
    monthlyRecurringListCharge,
    oneTimeListCharge,
  } = deliveryOrder;
  const statusInfo = getDeliveryOrderStatusInfo(status.toString());
  const isBillingPeriodOrder = orderContainsBillingPeriod(customerOrder);
  const recurringChargesTitle = isBillingPeriodOrder ? t.J4WW(recurringChargesMsg) : t.P6BC(monthlyChargesMsg);
  const trackingCodes = details.trackingCodes ?? [];

  return (
    <div className="of-delivery-order-details--section" key="of-delivery-order--section-general">
      <CL.Description
        items={[
          {
            title: t.ASQT(statusMsg),
            description: <Status color={statusInfo.color} textAfterBadge={statusInfo.text} />,
          },
          {
            title: t.VXHX('Postal shipping code'),
            description: trackingCodes.length > 0 ? <TrackingCodes trackingCodes={trackingCodes} /> : undefined,
          },
          {
            title: t.IY5V(customerMsg),
            description: deliveryOrder.deliveryOrderContact,
          },
          {
            title: t.C001(orderMsg),
            description: (
              <Link
                to={`${generatePath(paths.CUSTOMER_ORDER, { orderId: customerOrderDisplayId! })}${mdmId ? '?mdmId=' + mdmId : ''}`}
                id="customer-order-display"
                onClick={e => {
                  e.preventDefault();
                  onClickCustomerOrder();
                }}
              >
                {customerOrderDisplayId}
              </Link>
            ),
          },
          {
            title: t.F1NC(orderDateMsg),
            description: deliveryOrderCreated ? formatTimestampToUTCDDMMYYYY(deliveryOrderCreated) : undefined,
          },
          {
            title: eppOrder ? t.HMZZ('Contractual price for company, VAT 0%') : recurringChargesTitle,
            description: isApprover
              ? getPriceAsText(
                  monthlyRecurringCharge,
                  monthlyRecurringListCharge,
                  true,
                  '',
                  deliveryOrder.billingPeriod
                )
              : undefined,
          },
          {
            title: t.GOBY(oneTimePaymentMsg),
            description: isApprover && !eppOrder ? getPriceAsText(oneTimeCharge, oneTimeListCharge) : undefined,
          },
          {
            title: t.CVAH('Price for user'),
            description: isApprover
              ? getPriceAsText(employeeMonthlyRecurringCharge, undefined, true)
              : getEmployeePeriodicPriceAsText(employeeMonthlyRecurringCharge),
          },
          {
            title: t.K8DS('Order type'),
            description: eppOrder ? 'EPP' : undefined,
          },
        ].filter(item => !!item && item.description)}
      />
    </div>
  );
};
